import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

export interface AgGridTableRefreshContextValue {
  registerHandler: (handler: (() => void) | null) => void;
  triggerRefresh: () => void;
  isTableLoading: boolean;
  setIsTableLoading: (isLoading: boolean) => void;
}

const AgGridTableRefreshContext = createContext<AgGridTableRefreshContextValue | null>(null);

export const AgGridTableRefreshProvider = ({ children }: { children: ReactNode }) => {
  const handlerRef = useRef<(() => void) | null>(null);
  const [isTableLoading, setIsTableLoading] = useState(false);

  const registerHandler = useCallback((handler: (() => void) | null) => {
    handlerRef.current = handler;
  }, []);

  const triggerRefresh = useCallback(() => {
    handlerRef.current?.();
  }, []);

  const value = useMemo(
    () => ({
      registerHandler,
      triggerRefresh,
      isTableLoading,
      setIsTableLoading,
    }),
    [isTableLoading, registerHandler, triggerRefresh],
  );

  return (
    <AgGridTableRefreshContext.Provider value={value}>
      {children}
    </AgGridTableRefreshContext.Provider>
  );
};

export const useAgGridTableRefresh = (): AgGridTableRefreshContextValue => {
  const context = useContext(AgGridTableRefreshContext);
  if (!context) {
    throw new Error("useAgGridTableRefresh must be used within AgGridTableRefreshProvider");
  }
  return context;
};

export const useOptionalAgGridTableRefresh = (): AgGridTableRefreshContextValue | null =>
  useContext(AgGridTableRefreshContext);
